<template>
  <div>
    <f7-link @click="isPopupOpened = true">
      <f7-icon icon="icon-setting" />
    </f7-link>

    <f7-popup v-model:opened="isPopupOpened">
      <f7-page>
        <template #fixed>
          <TheNavbar
            :nav-title="t('common.configuration')"
            :popup-close-btn-enable="true"
          />
        </template>

        <!-- Ajustes -->
        <f7-card :title="t('common.settings')">
          <div class="padding-horizontal padding-bottom">
            <f7-list inset>
              <f7-list-input
                :label="t('appConfiguration.common.webServiceUrl')"
                :placeholder="t('appConfiguration.common.wsPlaceholder')"
                outline
                :value="urlAppLocal != null ? urlAppLocal : ''"
                @input="urlAppLocal = /** @type {any} */ ($event.target).value"
                @change="setDbInfoText(/** @type {any} */ ($event.target).value)"
              >
                <template
                  v-if="dbInfoText"
                  #info
                >
                  <div>
                    {{ `BD: ${dbInfoText}` }}
                  </div>
                </template>
              </f7-list-input>
            </f7-list>

            <f7-list
              inset
              no-chevron
            >
              <label class="item-title item-label margin-left">
                {{ t('appConfiguration.common.languageSettings') }}
              </label>

              <f7-list-item
                :title="t('appConfiguration.common.languageSettings')"
                outline
                smart-select
                :smart-select-params="{
                  openIn: 'popover',
                  searchbar: false,
                  closeOnSelect: true,
                }"
              >
                <select
                  name="language"
                  @change="selectLanguage(/** @type {any} */ ($event.target).value)"
                >
                  <option
                    v-for="lang in LANGUAGES"
                    :key="lang.Code"
                    :value="lang.Code"
                    :selected="lang.Code === language.Code"
                  >
                    {{ lang.Name }}
                  </option>
                </select>
              </f7-list-item>
            </f7-list>
          </div>
        </f7-card>

        <!-- Opciones de inicio de sesión -->
        <f7-card :title="t('appConfiguration.common.loginOptions')">
          <div class="padding-horizontal padding-bottom">
            <f7-list
              inset
              dividers
            >
              <f7-list-item
                :class="{ 'disabled-element': !dispositivoSoportaNFC }"
                :title="t('appConfiguration.common.allowNfcLogin')"
              >
                <template #after>
                  <f7-toggle
                    v-if="dispositivoSoportaNFC && dispositivoConNFCHabilitado"
                    color="gray"
                    :checked="allowNFCLogin"
                    @change="allowNFCLogin = $event.target.checked"
                  />

                  <f7-button
                    v-if="dispositivoSoportaNFC && !dispositivoConNFCHabilitado"
                    fill
                    small
                    @click="checkNFCStatus"
                  >
                    {{ t('appConfiguration.common.checkNFCStatus') }}
                  </f7-button>
                </template>

                <template #footer>
                  <span v-if="!dispositivoSoportaNFC">
                    {{ t('nfcReading.notCompatibleDevice') }}
                  </span>

                  <span v-if="dispositivoSoportaNFC && !dispositivoConNFCHabilitado">
                    {{ t('nfcReading.notEnabled') }}
                  </span>
                </template>
              </f7-list-item>

              <f7-list-item :title="t('appConfiguration.common.resetAppData')">
                <template #after>
                  <f7-button
                    fill
                    small
                    @click="resetApplicationData"
                  >
                    {{ t('common.restore') }}
                  </f7-button>
                </template>
              </f7-list-item>
            </f7-list>
          </div>
        </f7-card>

        <div class="display-flex justify-content-center">
          <f7-button
            fill
            @click="handleSave"
          >
            {{ t('common.save').toUpperCase() }}
          </f7-button>
        </div>
      </f7-page>
    </f7-popup>
  </div>
</template>

<script setup>
// @ts-check
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import db from '@/js/services/contracts/Database';
import { callInfoApp } from '@/js/services/APIErp';
import { LANGUAGES } from '@erpagroapp/common';
import { deleteDatabaseAndClearLocalStorage } from '@/js/services/contracts/ResetAppData';
import { DispositivoConNFCHabilitado, DispositivoSoportaNFC } from '@/js/common/HiNFCSupport';
import { ConfirmDialogGH, HiNotification } from '@/js/common/HINotificationSupport';
import { logError } from '@/js/utils/log';
import { resetLocalSettings, useSettingRef } from '@erpagroapp/settings';

import TheNavbar from '@/components/TheNavbar.vue';

/** @import { Ref } from 'vue' */

const emit = defineEmits([
  'on-success',
]);

const { t } = useI18n();
const language = useSettingRef('App.Idioma');
const urlApp = useSettingRef('App.URLWebService');

const hasInternalConfigurationsAccess = useSettingRef('ConfiguracionesInternas.AccesoHabilitado');
const showDatabaseConnection = useSettingRef('ConfiguracionesInternas.MostrarBaseDatosUsada');
const canShowDatabaseConnection = computed(() => hasInternalConfigurationsAccess.value
  && showDatabaseConnection.value);

const allowNFCLogin = useSettingRef('App.PermitirLoginNFC');

const isPopupOpened = ref(false);
/** @type {Ref<string | null>} */
const urlAppLocal = ref('');
const dispositivoSoportaNFC = ref(false);
const dispositivoConNFCHabilitado = ref(false);
const dbInfoText = ref('');

watch(canShowDatabaseConnection, (value) => {
  if (value) {
    setDbInfoText(urlAppLocal.value);
  } else {
    dbInfoText.value = '';
  }
});

watch(isPopupOpened, (value) => {
  if (value) {
    setInitialValues();
    checkNFCStatus();
  }
});
/**
 * Selects a language.
 * @param {string} lang - The language to select.
 * @returns {void}
 */
function selectLanguage(lang) {
  const foundLanguage = LANGUAGES.find((item) => item.Code === lang);
  if (foundLanguage) {
    language.value = foundLanguage;
  }
}

function setInitialValues() {
  urlAppLocal.value = urlApp.value ?? '';
  setDbInfoText(urlAppLocal.value);
}

async function checkNFCStatus() {
  dispositivoSoportaNFC.value = await DispositivoSoportaNFC();
  dispositivoConNFCHabilitado.value = await DispositivoConNFCHabilitado();
}

async function handleSave() {
  if (urlAppLocal.value === '') {
    HiNotification.Alert(t('appConfiguration.info.noWsUrlEntered'), t('appConfiguration.info.urlFieldIsEmpty'));
    return;
  }

  if (urlAppLocal.value === urlApp.value) {
    emit('on-success');
    isPopupOpened.value = false;
  }

  if (urlAppLocal.value !== urlApp.value) {
    const res = await ConfirmDialogGH(
      t('appConfiguration.info.thisActionWillDeleteAllData'),
      t('appConfiguration.info.syncUrlModified'),
    );

    if (res) {
      const resultReset = await deleteDatabaseAndClearLocalStorage({
        preserveAppConfig: true,
      });
      db.open();
      if (resultReset) {
        resetLocalSettings({
          considerResetOnLogout: false,
        });
        urlApp.value = urlAppLocal.value;

        emit('on-success');
        isPopupOpened.value = false;
      }
    } else {
      urlAppLocal.value = urlApp.value;
    }
  }
}

async function resetApplicationData() {
  const res = await ConfirmDialogGH(
    t('appConfiguration.info.thisActionWillDeleteAllData'),
    t('appConfiguration.info.resetData'),
  );

  if (res) {
    const resultReset = await deleteDatabaseAndClearLocalStorage();
    if (resultReset) {
      window.location.reload();
      db.open();
    }
  }
}

/**
 * Sets the database information text.
 * @param {string | null} value - The value to set as database information.
 */
async function setDbInfoText(value) {
  if (!value || !canSetDBInfoText(value)) {
    dbInfoText.value = '';
    return;
  }

  try {
    const { data } = await callInfoApp(value);

    if (!canSetDBInfoText(value)) {
      dbInfoText.value = '';
      return;
    }

    const info = data.Table1[0];
    dbInfoText.value = `${info['Server Name']} — ${info['Database Name']}`;
  } catch (error) {
    logError(error);
    dbInfoText.value = '';
  }
}

/**
 * Checks if the database information can be set.
 * @param {string | null} value - The value to check.
 * @returns {boolean} `true` if the database information can be set; otherwise, `false`.
 */
function canSetDBInfoText(value) {
  return canShowDatabaseConnection.value
    && !!value;
}
</script>

<style scoped>
.icon-setting {
  background-image: url("@/static/img/icon_setting.png");
  width: 32px;
  height: 32px;
}

.list {
  /* This will make the inputs take up the full width of the <li> element */
  --f7-list-item-padding-horizontal: 0;
  --f7-list-margin-vertical: var(--f7-typography-margin);
}

.disabled-element {
  opacity: 0.5;
  pointer-events: none;
}
</style>
